"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EthersLiquity = exports.EthersTransactionFailedError = void 0;
const lib_base_1 = require("@liquity/lib-base");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
const PopulatableEthersLiquity_1 = require("./PopulatableEthersLiquity");
const ReadableEthersLiquity_1 = require("./ReadableEthersLiquity");
const SendableEthersLiquity_1 = require("./SendableEthersLiquity");
/**
 * Thrown by {@link EthersLiquity} in case of transaction failure.
 *
 * @public
 */
class EthersTransactionFailedError extends lib_base_1.TransactionFailedError {
    constructor(message, failedReceipt) {
        super("EthersTransactionFailedError", message, failedReceipt);
    }
}
exports.EthersTransactionFailedError = EthersTransactionFailedError;
const waitForSuccess = async (tx) => {
    const receipt = await tx.waitForReceipt();
    if (receipt.status !== "succeeded") {
        throw new EthersTransactionFailedError("Transaction failed", receipt);
    }
    return receipt.details;
};
/**
 * Convenience class that combines multiple interfaces of the library in one object.
 *
 * @public
 */
class EthersLiquity {
    /** @internal */
    constructor(readable) {
        this._readable = readable;
        this.connection = readable.connection;
        this.populate = new PopulatableEthersLiquity_1.PopulatableEthersLiquity(readable);
        this.send = new SendableEthersLiquity_1.SendableEthersLiquity(this.populate);
    }
    /** @internal */
    static _from(connection) {
        if (EthersLiquityConnection_1._usingStore(connection)) {
            return new _EthersLiquityWithStore(ReadableEthersLiquity_1.ReadableEthersLiquity._from(connection));
        }
        else {
            return new EthersLiquity(ReadableEthersLiquity_1.ReadableEthersLiquity._from(connection));
        }
    }
    static async connect(signerOrProvider, optionalParams) {
        return EthersLiquity._from(await EthersLiquityConnection_1._connect(signerOrProvider, optionalParams));
    }
    hasStore() {
        return false;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalRedistributed} */
    getTotalRedistributed(overrides) {
        return this._readable.getTotalRedistributed(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTroveBeforeRedistribution} */
    getTroveBeforeRedistribution(address, overrides) {
        return this._readable.getTroveBeforeRedistribution(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTrove} */
    getTrove(address, overrides) {
        return this._readable.getTrove(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getNumberOfTroves} */
    getNumberOfTroves(overrides) {
        return this._readable.getNumberOfTroves(overrides);
    }
    getReceiptPerUnderlyingRatios(addresses, vaultTokens, overrides) {
        return this._readable.getReceiptPerUnderlyingRatios(addresses, vaultTokens, overrides);
    }
    getUnderlyingPerReceiptRatios(addresses, vaultTokens, overrides) {
        return this._readable.getUnderlyingPerReceiptRatios(addresses, vaultTokens, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getPrice} */
    getCollPrice(address, overrides) {
        return this._readable.getCollPrice(address, overrides);
    }
    getPrices(addresses, overrides) {
        return this._readable.getPrices(addresses, overrides);
    }
    getUnderlyingDecimals(vaultTokens, overrides) {
        return this._readable.getUnderlyingDecimals(vaultTokens, overrides);
    }
    getUnderlyingTokens(vaultTokens, overrides) {
        return this._readable.getUnderlyingTokens(vaultTokens, overrides);
    }
    getCollPrices(addresses, vaultTokens, underlyingDecimals, overrides) {
        return this._readable.getCollPrices(addresses, vaultTokens, underlyingDecimals, overrides);
    }
    getBalances(addresses, overrides) {
        return this._readable.getBalances(addresses, overrides);
    }
    getLPBalances(addresses, overrides) {
        return this._readable.getLPBalances(addresses, overrides);
    }
    /** @internal */
    _getActivePool(overrides) {
        return this._readable._getActivePool(overrides);
    }
    /** @internal */
    _getDefaultPool(overrides) {
        return this._readable._getDefaultPool(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotal} */
    getTotal(overrides) {
        return this._readable.getTotal(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getStabilityDeposit} */
    getStabilityDeposit(address, overrides) {
        return this._readable.getStabilityDeposit(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFarm} */
    getFarm(address, overrides) {
        return this._readable.getFarm(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFarm} */
    getBoostedFarm(address, overrides) {
        return this._readable.getBoostedFarm(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getVEYETIStake} */
    getVEYETIStake(address, overrides) {
        return this._readable.getVEYETIStake(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getPoolRewardRate} */
    getPoolRewardRate(overrides) {
        return this._readable.getPoolRewardRate(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingStabilityPoolYETIReward} */
    getRemainingStabilityPoolYETIReward(overrides) {
        return this._readable.getRemainingStabilityPoolYETIReward(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getYUSDInStabilityPool} */
    getYUSDInStabilityPool(overrides) {
        return this._readable.getYUSDInStabilityPool(overrides);
    }
    getDepositFee(VCInput, VCOutput, overrides) {
        return this._readable.getDepositFee(VCInput, VCOutput, overrides);
    }
    getEstimatedFarmRewards(amount, time, overrides) {
        return this._readable.getEstimatedFarmRewards(amount, time, overrides);
    }
    // getRiskiestTrove(overrides?: EthersCallOverrides): Promise<TroveWithPendingRedistribution> {
    //   return this._readable.getRiskiestTrove(overrides)
    // }
    getEstimatedVeYetiRewards(amount, time, overrides) {
        return this._readable.getEstimatedVeYetiRewards(amount, time, overrides);
    }
    getEstimatedYETIPoolRewards(amount, time, overrides) {
        return this._readable.getEstimatedYETIPoolRewards(amount, time, overrides);
    }
    getBalanceERC20(address) {
        return this._readable.getBalanceERC20(address);
    }
    getLP(address) {
        return this._readable.getLP(address);
    }
    hasClaimableCollateral(address) {
        return this._readable.hasClaimableCollateral(address);
    }
    getRedemptionBonus(address) {
        return this._readable.getRedemptionBonus(address);
    }
    getRedemptionFeeRate(overrides) {
        return this._readable.getRedemptionFeeRate(overrides);
    }
    getReceiptPerUnderlying(address) {
        return this._readable.getReceiptPerUnderlying(address);
    }
    getUnderlyingPerReceipt(address) {
        return this._readable.getUnderlyingPerReceipt(address);
    }
    getUnderlyingDecimal(address) {
        return this._readable.getUnderlyingDecimal(address);
    }
    getUnderlyingToken(address) {
        return this._readable.getUnderlyingToken(address);
    }
    getSortedTroveHead() {
        return this._readable.getSortedTroveHead();
    }
    getSortedTroveTail() {
        return this._readable.getSortedTroveTail();
    }
    getSortedTroveSize() {
        return this._readable.getSortedTroveSize();
    }
    getSortedTroveNext(id) {
        return this._readable.getSortedTroveNext(id);
    }
    getSortedTrovePrev(id) {
        return this._readable.getSortedTrovePrev(id);
    }
    getCurrentAICR(borrower) {
        return this._readable.getCurrentAICR(borrower);
    }
    getTroveDebt(borrower) {
        return this._readable.getTroveDebt(borrower);
    }
    getAllowanceOf(owner, token, spender, amount) {
        return this._readable.getAllowanceOf(owner, token, spender, amount);
    }
    getYusdAllowance(from, to, amount) {
        return this._readable.getYusdAllowance(from, to, amount);
    }
    getYETIPrice() {
        return this._readable.getYETIPrice();
    }
    getYUSDPrice() {
        return this._readable.getYUSDPrice();
    }
    getGlobalBoostFactor() {
        return this._readable.getGlobalBoostFactor();
    }
    getDecayedBoost(address) {
        return this._readable.getDecayedBoost(address);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getYUSDBalance} */
    getYUSDBalance(address, overrides) {
        return this._readable.getYUSDBalance(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getYETIBalance} */
    getYETIBalance(address, overrides) {
        return this._readable.getYETIBalance(address, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getUniTokenBalance} */
    // getUniTokenBalance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.getUniTokenBalance(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetUniTokenBalance} */
    // pngGetUniTokenBalance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.pngGetUniTokenBalance(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetUniTokenBalance} */
    // tjGetUniTokenBalance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.tjGetUniTokenBalance(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getUniTokenAllowance} */
    // getUniTokenAllowance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.getUniTokenAllowance(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetUniTokenAllowance} */
    // pngGetUniTokenAllowance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.pngGetUniTokenAllowance(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetUniTokenAllowance} */
    // tjGetUniTokenAllowance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.tjGetUniTokenAllowance(address, overrides);
    // }
    /** @internal */
    // _getRemainingLiquidityMiningYETIRewardCalculator(
    //   overrides?: EthersCallOverrides
    // ): Promise<(blockTimestamp: number) => Decimal> {
    //   return this._readable._getRemainingLiquidityMiningYETIRewardCalculator(overrides);
    // }
    /** @internal */
    // _pngGetRemainingLiquidityMiningYETIRewardCalculator(
    //   overrides?: EthersCallOverrides
    // ): Promise<(blockTimestamp: number) => Decimal> {
    //   return this._readable._pngGetRemainingLiquidityMiningYETIRewardCalculator(overrides);
    // }
    /** @internal */
    // _tjGetRemainingLiquidityMiningYETIRewardCalculator(
    //   overrides?: EthersCallOverrides
    // ): Promise<(blockTimestamp: number) => Decimal> {
    //   return this._readable._tjGetRemainingLiquidityMiningYETIRewardCalculator(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingLiquidityMiningYETIReward} */
    // getRemainingLiquidityMiningYETIReward(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.getRemainingLiquidityMiningYETIReward(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetRemainingLiquidityMiningYETIReward} */
    // pngGetRemainingLiquidityMiningYETIReward(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.pngGetRemainingLiquidityMiningYETIReward(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetRemainingLiquidityMiningYETIReward} */
    // tjGetRemainingLiquidityMiningYETIReward(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.tjGetRemainingLiquidityMiningYETIReward(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningStake} */
    // getLiquidityMiningStake(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.getLiquidityMiningStake(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetLiquidityMiningStake} */
    // pngGetLiquidityMiningStake(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.pngGetLiquidityMiningStake(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningStake} */
    // tjGetLiquidityMiningStake(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.tjGetLiquidityMiningStake(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalStakedUniTokens} */
    // getTotalStakedUniTokens(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.getTotalStakedUniTokens(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetTotalStakedUniTokens} */
    // pngGetTotalStakedUniTokens(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.pngGetTotalStakedUniTokens(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetTotalStakedUniTokens} */
    // tjGetTotalStakedUniTokens(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.tjGetTotalStakedUniTokens(overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningYETIReward} */
    // getLiquidityMiningYETIReward(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   return this._readable.getLiquidityMiningYETIReward(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetLiquidityMiningYETIReward} */
    // pngGetLiquidityMiningYETIReward(
    //   address?: string,
    //   overrides?: EthersCallOverrides
    // ): Promise<Decimal> {
    //   return this._readable.pngGetLiquidityMiningYETIReward(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetLiquidityMiningYETIReward} */
    // tjGetLiquidityMiningYETIReward(
    //   address?: string,
    //   overrides?: EthersCallOverrides
    // ): Promise<Decimal> {
    //   return this._readable.tjGetLiquidityMiningYETIReward(address, overrides);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getCollateralSurplusBalance} */
    getCollateralSurplusBalancee(overrides) {
        return this._readable.getCollateralSurplusBalancee(overrides);
    }
    getTroves(params, overrides) {
        return this._readable.getTroves(params, overrides);
    }
    /** @internal */
    _getBlockTimestamp(blockTag) {
        return this._readable._getBlockTimestamp(blockTag);
    }
    /** @internal */
    _getFeesFactory(overrides) {
        return this._readable._getFeesFactory(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFees} */
    getFees(overrides) {
        return this._readable.getFees(overrides);
    }
    getVaultTokens(whitelistedCollaterals, overrides) {
        return this._readable.getVaultTokens(whitelistedCollaterals, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getWhitelistedCollaterals} */
    getWhitelistedCollaterals(overrides) {
        return this._readable.getWhitelistedCollaterals(overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getICR} */
    getICR(userAddress, overrides) {
        return this._readable.getICR(userAddress, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getVcValue} */
    getVcValue(userAddress, overrides) {
        return this._readable.getVcValue(userAddress, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getSafetyRatios} */
    getSafetyRatios(addresses, overrides) {
        return this._readable.getSafetyRatios(addresses, overrides);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getSafetyRatios} */
    getRecoveryRatios(addresses, overrides) {
        return this._readable.getRecoveryRatios(addresses, overrides);
    }
    getDecimals(addresses, overrides) {
        return this._readable.getDecimals(addresses, overrides);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.openTrove}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    openTrove(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        return this.send
            .openTrove(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides)
            .then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.openTroveLeverUp}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    openTroveLeverUp(params, ICRWithFees, troveOpen, maxBorrowingRateOrOptionalParams, overrides) {
        return this.send
            .openTroveLeverUp(params, ICRWithFees, troveOpen, maxBorrowingRateOrOptionalParams, overrides)
            .then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.closeTrove}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    closeTrove(overrides) {
        return this.send.closeTrove(overrides).then(waitForSuccess);
    }
    /**
    * {@inheritDoc @liquity/lib-base#TransactableLiquity.closeTroveUnleverUp}
    *
    * @throws
    * Throws {@link EthersTransactionFailedError} in case of transaction failure.
    * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
    */
    closeTroveUnleverUp(params, overrides) {
        return this.send.closeTroveUnleverUp(params, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.adjustTrove}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    adjustTrove(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        return this.send
            .adjustTrove(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides)
            .then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.addCollLeverUp}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    addCollLeverUp(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        return this.send
            .addCollLeverUp(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides)
            .then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.addCollLeverUp}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    withdrawCollUnleverUp(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides) {
        return this.send
            .withdrawCollUnleverUp(params, ICRWithFees, maxBorrowingRateOrOptionalParams, overrides)
            .then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.depositCollateral}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    depositCollateral(collaterals, ICRWithFees, overrides) {
        return this.send.depositCollateral(collaterals, ICRWithFees, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.withdrawCollateral}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    withdrawCollateral(collaterals, ICRWithFees, overrides) {
        return this.send.withdrawCollateral(collaterals, ICRWithFees, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.borrowYUSD}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    borrowYUSD(amount, ICRWithFees, maxBorrowingRate, overrides) {
        return this.send.borrowYUSD(amount, ICRWithFees, maxBorrowingRate, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.repayYUSD}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    repayYUSD(amount, ICRWithFees, overrides) {
        return this.send.repayYUSD(amount, ICRWithFees, overrides).then(waitForSuccess);
    }
    /** @internal */
    setPrice(price, overrides) {
        return this.send.setPrice(price, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.liquidate}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    liquidate(address, liquidator, overrides) {
        return this.send.liquidate(address, liquidator, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.liquidateUpTo}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    // liquidateUpTo(
    //   maximumNumberOfTrovesToLiquidate: number,
    //   overrides?: EthersTransactionOverrides
    // ): Promise<LiquidationDetails> {
    //   return this.send.liquidateUpTo(maximumNumberOfTrovesToLiquidate, overrides).then(waitForSuccess);
    // }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.depositYUSDInStabilityPool}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    depositYUSDInStabilityPool(amount, overrides) {
        return this.send.depositYUSDInStabilityPool(amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.withdrawYUSDFromStabilityPool}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    withdrawYUSDFromStabilityPool(amount, overrides) {
        return this.send.withdrawYUSDFromStabilityPool(amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.withdrawGainsFromStabilityPool}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    withdrawGainsFromStabilityPool(overrides) {
        return this.send.withdrawGainsFromStabilityPool(overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.claimRewardsSwap}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    claimRewardsSwap(amount, swapAssets, reinvest, overrides) {
        return this.send.claimRewardsSwap(amount, swapAssets, reinvest, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.stakeLPTokens}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    stakeLPTokens(amount, overrides) {
        return this.send.stakeLPTokens(amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.withdrawLPTokens}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    withdrawLPTokens(amount, overrides) {
        return this.send.withdrawLPTokens(amount, overrides).then(waitForSuccess);
    }
    /**
    * {@inheritDoc @liquity/lib-base#TransactableLiquity.stakeLPTokens}
    *
    * @throws
    * Throws {@link EthersTransactionFailedError} in case of transaction failure.
    * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
    */
    stakeLPTokensOldFarm(amount, overrides) {
        return this.send.stakeLPTokensOldFarm(amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.withdrawLPTokens}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    withdrawLPTokensOldFarm(amount, overrides) {
        return this.send.withdrawLPTokensOldFarm(amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.getFarmRewards}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    getFarmRewards(overrides) {
        return this.send.getFarmRewards(overrides).then(waitForSuccess);
    }
    getOldFarmRewards(overrides) {
        return this.send.getOldFarmRewards(overrides).then(waitForSuccess);
    }
    getVeYetiStakeReward(overrides) {
        return this.send.getVeYetiStakeReward(overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.getFarmRewards}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    updateVEYETI(params, overrides) {
        return this.send.updateVEYETI(params, overrides).then(waitForSuccess);
    }
    notifyAllRewarders(overrides) {
        return this.send.notifyAllRewarders(overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.transferCollateralGainToTrove}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    transferCollateralGainToTrove(overrides) {
        return this.send.transferCollateralGainToTrove(overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.sendYUSD}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    sendYUSD(toAddress, amount, overrides) {
        return this.send.sendYUSD(toAddress, amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.sendYETI}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    sendYETI(toAddress, amount, overrides) {
        return this.send.sendYETI(toAddress, amount, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.redeemYUSD}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    redeemYUSD(amount, maxRedemptionRate, overrides) {
        return this.send.redeemYUSD(amount, maxRedemptionRate, overrides).then(waitForSuccess);
    }
    /**
     * {@inheritDoc @liquity/lib-base#TransactableLiquity.claimCollateralSurplus}
     *
     * @throws
     * Throws {@link EthersTransactionFailedError} in case of transaction failure.
     * Throws {@link EthersTransactionCancelledError} if the transaction is cancelled or replaced.
     */
    claimCollateralSurplus(overrides) {
        return this.send.claimCollateralSurplus(overrides).then(waitForSuccess);
    }
}
exports.EthersLiquity = EthersLiquity;
class _EthersLiquityWithStore extends EthersLiquity {
    constructor(readable) {
        super(readable);
        this.store = readable.store;
    }
    hasStore(store) {
        return store === undefined || store === this.connection.useStore;
    }
}
//# sourceMappingURL=EthersLiquity.js.map